import { useEffect, useRef } from 'react';
import { useLocation } from '@/lib/router-compat';
import { useAuth } from '@/context/AuthContext';
import { getVisitorId, getSessionId, peekSessionId, getClientEnvironment } from '@/lib/visitorId';
import { reportAiReferralToAnalytics } from '@/lib/aiReferral';

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-visit`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

/** Visits here are still recorded, but flagged so they stay out of public traffic numbers. */
const INTERNAL_PREFIXES = ['/admin', '/plus'];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const resolvePageType = (pathname: string): string => {
  if (pathname === '/') return 'home';
  const segment = pathname.split('/').filter(Boolean)[0] || 'home';
  const map: Record<string, string> = {
    article: 'article',
    markets: 'markets',
    podcast: 'podcast',
    podcasts: 'podcast',
    weekly: 'weekly',
    leaderboard: 'leaderboard',
    profile: 'profile',
    premium: 'premium',
    bookmarks: 'bookmarks',
    search: 'search',
  };
  return map[segment] || segment;
};

// scrollHeight is a layout-forcing read, so it only happens here — once per
// saved visit — never inside the per-scroll handler.
const scrollDepth = (scrollY: number): number => {
  const height = document.documentElement.scrollHeight - window.innerHeight;
  if (height <= 0) return 100;
  return Math.min(100, Math.max(0, Math.round((scrollY / height) * 100)));
};

const send = (body: Record<string, unknown>, beacon = false) => {
  const payload = JSON.stringify(body);
  if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    if (navigator.sendBeacon(`${FUNCTION_URL}?apikey=${ANON_KEY}`, blob)) return Promise.resolve(null);
  }
  return fetch(FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
    body: payload,
    keepalive: true,
  })
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null);
};

/** Records anonymous and signed-in page views for the admin visitors report. */
export const useVisitTracker = () => {
  const location = useLocation();
  const { user } = useAuth();

  const currentView = useRef<{ id: string; startedAt: number; maxScrollY: number } | null>(null);
  const userIdRef = useRef<string | undefined>(user?.id);
  userIdRef.current = user?.id;
  const pathRef = useRef<string>(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    const onScroll = () => {
      if (!currentView.current) return;
      // window.scrollY is free to read; page height is only measured at flush time.
      currentView.current.maxScrollY = Math.max(currentView.current.maxScrollY, window.scrollY || 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Outbound links and story opens, recorded as click events.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.('a') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      let isOutbound = false;
      let absolute = href;
      try {
        const url = new URL(href, window.location.origin);
        absolute = url.href;
        isOutbound = url.origin !== window.location.origin;
      } catch {
        return;
      }

      const isStory = href.startsWith('/article/') || absolute.includes('/article/');
      if (!isOutbound && !isStory) return;

      const pathname = pathRef.current;
      send(
        {
          type: 'click',
          visitorId: getVisitorId(),
          sessionId: peekSessionId(),
          userId: userIdRef.current ?? null,
          path: pathname,
          linkUrl: absolute.slice(0, 1024),
          title: (anchor.textContent || '').trim().slice(0, 200) || document.title,
          pageType: isOutbound ? 'outbound' : 'story-open',
          isInternal: INTERNAL_PREFIXES.some((prefix) => pathname.startsWith(prefix)),
        },
        true,
      );
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    const pathname = location.pathname;
    const isInternal = INTERNAL_PREFIXES.some((prefix) => pathname.startsWith(prefix));

    let cancelled = false;

    const flushPrevious = (beacon = false) => {
      const view = currentView.current;
      if (!view) return;
      currentView.current = null;
      send(
        {
          type: 'update',
          viewId: view.id,
          duration: Math.round((Date.now() - view.startedAt) / 1000),
          scroll: scrollDepth(Math.max(view.maxScrollY, window.scrollY || 0)),
        },
        beacon,
      );
    };

    flushPrevious();

    const timeout = setTimeout(async () => {
      const params = new URLSearchParams(location.search);
      const { sessionId, pageIndex } = getSessionId();
      const env = getClientEnvironment();
      const articleSegment = pathname.startsWith('/article/') ? pathname.split('/')[2] : null;
      const aiSource = reportAiReferralToAnalytics(pathname);

      const result = (await send({
        visitorId: getVisitorId(),
        sessionId,
        pageIndex,
        userId: userIdRef.current ?? null,
        path: pathname,
        title: document.title,
        pageType: resolvePageType(pathname),
        articleId: articleSegment && UUID_RE.test(articleSegment) ? articleSegment : null,
        referrer: document.referrer || null,
        utmSource: params.get('utm_source'),
        utmMedium: params.get('utm_medium'),
        utmCampaign: params.get('utm_campaign'),
        aiSource,
        isInternal,
        ...env,
      })) as { viewId?: string } | null;

      if (!cancelled && result?.viewId) {
        currentView.current = { id: result.viewId, startedAt: Date.now(), maxScrollY: window.scrollY || 0 };
      }
    }, 400);

    const onHide = () => {
      if (document.visibilityState === 'hidden') flushPrevious(true);
    };
    const onPageHide = () => flushPrevious(true);

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);
    document.addEventListener('visibilitychange', onHide);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
      document.removeEventListener('visibilitychange', onHide);
      flushPrevious();
    };
  }, [location.pathname, location.search]);
};
